"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.getOrCreateBridgeAccess = getOrCreateBridgeAccess;
exports.ensureBridgeAccess = ensureBridgeAccess;
const patchedPryv_ts_1 = require("../patchedPryv.js");
const logger = __importStar(require("../logger.js"));
const errors_ts_1 = require("../errors.js");
const HDSModelInitAndSingleton_ts_1 = require("../HDSModel/HDSModelInitAndSingleton.js");
const StreamsTools_ts_1 = require("../toolkit/StreamsTools.js");
const offerStreams_ts_1 = require("./offerStreams.js");
/**
 * Get or create a bridge access on a user's account.
 * Looks up by name; if found, returns existing. If not, creates new.
 *
 * @param connection - Pryv connection to the user's account (personal token)
 * @param options - access configuration
 */
async function getOrCreateBridgeAccess(connection, options) {
    const accesses = await connection.apiOne('accesses.get', {}, 'accesses');
    const existing = accesses.find((a) => a.name === options.name);
    if (existing) {
        return {
            apiEndpoint: existing.apiEndpoint,
            accessId: existing.id,
            created: false,
            updated: false
        };
    }
    // Build the model's stream hierarchy BEFORE minting the access.
    //
    // Pryv's `accesses.create` auto-creates any stream named by a permission's
    // `defaultName` — but FLAT, at root. So on an account that does not already have
    // the tree, a permission on `body-temperature-basal` yielded a root-level stream
    // instead of `body > body-temperature > body-temperature-basal`. The bridge cannot
    // repair it afterwards: its scoped access is `forbidden` from creating the model
    // parents, which is why bridge-tempdrop logged exactly that and carried on.
    //
    // The comment in bridge-tempdrop's ensureBaseStreams assumed "the webapp provisions
    // [the parents] when it mints the access". Nothing did — this is now the code that
    // makes that true, and it fixes every bridge, not just Tempdrop.
    //
    // Best-effort by design: a model that is not loaded, or a stream the model does not
    // know (a bridge's own home stream such as `bridge-tempdrop`), must not block the
    // connect. Those keep the existing flat-at-root behaviour, which is correct for them.
    await ensureModelStreamHierarchy(connection, options.permissions);
    const access = await connection.apiOne('accesses.create', {
        name: options.name,
        permissions: options.permissions,
        clientData: options.clientData || {}
    }, 'access');
    return {
        apiEndpoint: access.apiEndpoint,
        accessId: access.id,
        created: true,
        updated: false
    };
}
/**
 * Create the model-defined parent chain for every permission streamId the model
 * knows about, root-first, on the user's own (personal) connection.
 *
 * Idempotent: `item-already-exists` is the normal result on an account that already
 * has the tree. Never throws — see the rationale at the call site.
 */
async function ensureModelStreamHierarchy(connection, permissions) {
    let toCreate = [];
    try {
        const modelStreams = (0, HDSModelInitAndSingleton_ts_1.getModel)().streams;
        const seen = new Set();
        for (const permission of permissions) {
            const streamId = permission.streamId;
            if (typeof streamId !== 'string')
                continue;
            // Not a model stream (e.g. a bridge's own home stream) — leave it to defaultName.
            if (modelStreams.getDataById(streamId, false) == null)
                continue;
            // getParentsIds `unshift`s, so the chain is ALREADY root-first:
            // ['body', 'body-temperature', 'body-temperature-basal']. Iterate forward — a
            // parent must exist before its child, because `streams.create` calls in one
            // batch do not see each other and a forward reference fails
            // `unknown-referenced-resource`. (getNecessaryListForItems walks this backwards
            // only so it can `break` early on a known stream, and reverses afterwards.)
            const chain = modelStreams.getParentsIds(streamId, false, [streamId]);
            for (const id of chain) {
                if (seen.has(id))
                    continue;
                seen.add(id);
                const data = modelStreams.getDataById(id, false);
                if (data == null)
                    continue;
                toCreate.push({ id, parentId: data.parentId ?? null, name: data.name });
            }
        }
    }
    catch (e) {
        logger.warn(`getOrCreateBridgeAccess: could not resolve model streams, falling back to defaultName: ${e?.message ?? e}`);
        toCreate = [];
    }
    if (toCreate.length === 0)
        return;
    try {
        const results = await connection.api(toCreate.map((s) => ({ method: 'streams.create', params: s })));
        for (let i = 0; i < results.length; i++) {
            const r = results[i];
            if (r?.stream != null)
                continue;
            if (r?.error?.id === 'item-already-exists')
                continue;
            logger.warn(`getOrCreateBridgeAccess: could not create stream ${toCreate[i]?.id}: ${r?.error?.id ?? 'unknown'}`);
        }
    }
    catch (e) {
        logger.warn(`getOrCreateBridgeAccess: stream hierarchy creation failed: ${e?.message ?? e}`);
    }
}
/**
 * Create, on the user's own (personal) connection, every stream the permissions
 * name that the account does not have yet: what `accesses.create` does through
 * `defaultName`, and `accesses.update` does not.
 *
 * Without it, an access updated to a grant on a new stream points at nothing: the
 * update succeeds (the server does not check the stream exists), then the bridge,
 * whose scoped access cannot create a root stream, fails to provision its tree.
 *
 * - A stream the data model knows is created with its model chain, root-first, with
 *   the model's names and parents (`offerStreamsToCreate`), never as a flat
 *   `defaultName` copy. Existing streams, model or not, are left untouched.
 * - Any other stream is created at root, named by the permission's `defaultName`, as
 *   `accesses.create` would. Without a `defaultName` it is not created, as
 *   `accesses.create` would not; nor are `*` and store-prefixed (`:…`) ids.
 * - Idempotent: `item-already-exists` (for instance a trashed stream, which
 *   `streams.get` does not list) is fine.
 *
 * Throws an `HDSLibError` naming each stream that could not be created, so the
 * caller does not update an access its bridge cannot use.
 */
async function ensurePermissionStreams(connection, accessName, permissions) {
    const granted = permissions.filter((p) => typeof p?.streamId === 'string' && p.streamId !== '*' && !p.streamId.startsWith(':'));
    if (granted.length === 0)
        return;
    const roots = await connection.apiOne('streams.get', {}, 'streams');
    const existing = new Set();
    for (const s of (0, StreamsTools_ts_1.allStreamsAndChildren)(roots ?? []))
        existing.add(s.id);
    if (granted.every((p) => existing.has(p.streamId)))
        return;
    let modelStreams = null;
    try {
        modelStreams = (0, HDSModelInitAndSingleton_ts_1.getModel)().streams;
    }
    catch (e) {
        logger.warn(`ensureBridgeAccess: model not loaded, creating every missing stream from defaultName: ${e?.message ?? e}`);
    }
    const defaultNames = new Map(granted.map((p) => [p.streamId, p.defaultName]));
    const missing = granted.filter((p) => !existing.has(p.streamId));
    const wanted = modelStreams != null
        ? (0, offerStreams_ts_1.offerStreamsToCreate)(missing, modelStreams)
        : missing.map((p) => ({ id: p.streamId, name: p.streamId }));
    const toCreate = [];
    for (const s of wanted) {
        if (existing.has(s.id))
            continue;
        if (modelStreams?.getDataById(s.id, false) != null) {
            toCreate.push(s);
            continue;
        }
        const defaultName = defaultNames.get(s.id);
        if (defaultName == null)
            continue;
        toCreate.push({ id: s.id, name: defaultName });
    }
    if (toCreate.length === 0)
        return;
    let results;
    try {
        results = await connection.api(toCreate.map((s) => ({ method: 'streams.create', params: s })));
    }
    catch (e) {
        throw new errors_ts_1.HDSLibError(`ensureBridgeAccess "${accessName}": could not create the streams ${toCreate.map((s) => s.id).join(', ')}, access not updated`, e);
    }
    const failures = [];
    for (let i = 0; i < toCreate.length; i++) {
        const r = results?.[i];
        if (r?.stream != null || r?.error?.id === 'item-already-exists')
            continue;
        failures.push({ streamId: toCreate[i].id, error: r?.error ?? { id: 'unexpected-error', data: r } });
    }
    if (failures.length > 0) {
        const list = failures.map((f) => `${f.streamId} (${f.error?.id}${f.error?.message != null ? ': ' + f.error.message : ''})`).join(', ');
        throw new errors_ts_1.HDSLibError(`ensureBridgeAccess "${accessName}": could not create the stream(s) its permissions need, access not updated: ${list}`, { failures });
    }
    logger.info('Bridge access streams created', { name: accessName, streams: toCreate.map((s) => s.id) });
}
/**
 * Get or create a bridge access, with optional permission update detection.
 *
 * If the access exists and `updateIfDifferent` is set and permissions differ,
 * updates it in place via `accesses.update` (Plan 66). The access id becomes
 * composite (`<base>:<serial>`) but the token and apiEndpoint are preserved.
 *
 * With `updateIfDifferent`, the streams the permissions name are created first
 * when the account lacks them (`accesses.update`, unlike `accesses.create`, does
 * not create them) — also when the permissions already match, which is the state
 * an earlier update to a new grant left behind.
 *
 * Server-side `clientData` merge means any pre-existing keys on the access
 * (notably `previousAccessIds` from the legacy delete+create era) are
 * preserved automatically — we only send the keys we want to set.
 *
 * `StaleAccessIdError` handling: if another writer updates the access between
 * our `accesses.get` and our `accesses.update`, we refetch + retry once.
 * Two consecutive stale errors propagate.
 *
 * @param connection - Pryv connection to the user's account (personal token)
 * @param options - access configuration
 * @param options.updateIfDifferent - if true, update permissions in place when they differ (default: false)
 */
async function ensureBridgeAccess(connection, options) {
    let attempt = 0;
    let streamsEnsured = false;
    while (true) {
        const accesses = await connection.apiOne('accesses.get', {}, 'accesses');
        const existing = accesses.find((a) => a.name === options.name);
        if (!existing) {
            // Same reason as in getOrCreateBridgeAccess: build the model hierarchy before
            // `accesses.create`, or `defaultName` creates the streams flat at root.
            await ensureModelStreamHierarchy(connection, options.permissions);
            const access = await connection.apiOne('accesses.create', {
                name: options.name,
                permissions: options.permissions,
                clientData: options.clientData || {}
            }, 'access');
            return {
                apiEndpoint: access.apiEndpoint,
                accessId: access.id,
                created: true,
                updated: false
            };
        }
        if (options.updateIfDifferent && !streamsEnsured) {
            await ensurePermissionStreams(connection, options.name, options.permissions);
            streamsEnsured = true;
        }
        if (!options.updateIfDifferent || permissionsMatch(existing.permissions, options.permissions)) {
            return {
                apiEndpoint: existing.apiEndpoint,
                accessId: existing.id,
                created: false,
                updated: false
            };
        }
        // Update in place. Server merges clientData; we only send our new keys.
        // accesses.update's permissions schema is strict (rejects defaultName / name);
        // strip to canonical {streamId,level} | {feature,setting} regardless of caller input.
        const cleanedPermissions = options.permissions.map((p) => {
            if (p.streamId)
                return { streamId: p.streamId, level: p.level };
            if (p.feature)
                return { feature: p.feature, setting: p.setting };
            return p;
        });
        const updatePayload = { permissions: cleanedPermissions };
        if (options.clientData != null)
            updatePayload.clientData = options.clientData;
        try {
            logger.info('Bridge access permissions differ, updating', { name: options.name, id: existing.id });
            const updated = await connection.updateAccess(existing.id, updatePayload);
            return {
                apiEndpoint: updated.apiEndpoint,
                accessId: updated.id,
                created: false,
                updated: true
            };
        }
        catch (e) {
            if (e instanceof patchedPryv_ts_1.pryv.StaleAccessIdError && attempt === 0) {
                attempt++;
                logger.info('Bridge access stale on update, refetching and retrying once', { name: options.name });
                continue;
            }
            throw e;
        }
    }
}
/** Compare two permission arrays (order-independent) */
function permissionsMatch(a, b) {
    if (!a || !b)
        return false;
    if (a.length !== b.length)
        return false;
    const normalize = (p) => `${p.streamId || ''}:${p.level || ''}:${p.feature || ''}:${p.setting || ''}`;
    const setA = new Set(a.map(normalize));
    const setB = new Set(b.map(normalize));
    if (setA.size !== setB.size)
        return false;
    for (const item of setA) {
        if (!setB.has(item))
            return false;
    }
    return true;
}
